import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ShoppingBag, CreditCard, Tag, Package, ArrowRight, SquareStack } from 'lucide-react';
import ScrollReveal from './ScrollReveal';
import { useNavigate } from 'react-router-dom';

const ProductShowcase = () => {
  const navigate = useNavigate();
  const [activeProduct, setActiveProduct] = useState(0);

  const products = [
    {
      id: 'keychain',
      icon: ShoppingBag,
      name: 'NFC Keychains',
      tagline: 'Attach to your keys, share anywhere',
      price: '$24.99',
      features: ['Durable Acrylic Design', 'Custom Engraving', 'Lightweight & Comfortable', 'Built for Everyday Use', 'Tap. Share. Connect Instantly.'],
      image: '/images/nfc-keychains.png',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'card',
      icon: CreditCard,
      name: 'NFC Business Cards',
      tagline: 'Premium PVC cards that never expire',
      price: '$19.99',
      features: ['Credit Card Size', 'Premium Finish', 'Custom Design', 'Bulk Discounts (Contact for Quote)'],
      image: '/images/nfc-business-cards.png',
      color: 'from-teal-500 to-cyan-500'
    },
    {
      id: 'signs',
      icon: SquareStack,
      name: 'NFC Signs',
      tagline: 'Perfect for businesses and events',
      price: '$49.99',
      features: ['Durable Material', 'Custom Size Options', 'Easy Setup'],
      image: '/images/nfc-signs.png',
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <section id="product-showcase" className="py-12 bg-gradient-to-b from-black to-zinc-900 relative overflow-visible">
      <div className="container mx-auto px-6">
        <ScrollReveal>
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
            Choose Your Perfect NFC Solution
          </h2>
          <p className="text-lg text-gray-400 text-center mb-8 max-w-3xl mx-auto">
            From keychains to cards, we have the perfect NFC product for every professional
          </p>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {products.map((product, index) => (
            <div
              key={product.id}
              className="transform transition-all duration-150 ease-out"
              onMouseEnter={() => setActiveProduct(index)}
            >
              <Card className={`group bg-zinc-900 border-zinc-800 hover:border-blue-500/50 transition-all duration-150 overflow-hidden h-full flex flex-col ${
                activeProduct === index ? 'ring-2 ring-blue-500 shadow-xl shadow-blue-500/20' : ''
              }`}>
                  {/* Product Image */}
                  <div className="relative h-36 overflow-hidden bg-gradient-to-br from-zinc-900 to-zinc-800">
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-200"
                    />
                    
                    {/* Price Badge */}
                    <div className="absolute top-2 right-2 bg-black/80 backdrop-blur-sm text-white px-2.5 py-1 rounded-full font-bold text-xs">
                      {product.price}
                    </div>
                  </div>

                  <CardContent className="p-4 space-y-2 flex flex-col flex-1">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${product.color} opacity-10 flex items-center justify-center`}>
                      <product.icon className="w-5 h-5 text-white" />
                    </div>

                    {/* Product Info */}
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white mb-1 leading-tight">{product.name}</h3>
                      <p className="text-gray-400 text-xs mb-2 leading-tight">{product.tagline}</p>
                    </div>

                    {/* Features */}
                    <ul className="space-y-1 mb-3">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-1.5 text-xs text-gray-400 leading-tight">
                          <div className="w-1 h-1 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <Button 
                      onClick={() => navigate('/products')}
                      className={`w-full bg-gradient-to-r ${product.color} hover:opacity-90 text-white group text-xs py-2 h-auto`}
                    >
                      Shop {product.name.split(' ')[1]}
                      <ArrowRight className="ml-1 w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
            </div>
          ))}
        </div>

        {/* Product Navigation Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {products.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveProduct(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                activeProduct === index ? 'w-6 bg-blue-500' : 'w-1.5 bg-zinc-700 hover:bg-zinc-600'
              }`}
              aria-label={`View product ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
